import type { Pinia } from 'pinia';
import type { Context } from './@types/ssr';

import LocalForage from 'localforage';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createPinia } from 'pinia';

import { useUserStore } from '@/stores/pinia/useUserStore';
import Cookies from 'js-cookie';
/** Pinia Persisted State Plugin namespace à la jw/user. */
export const STORE_NAMESPACE = 'jw';

export async function createPiniaStore(context: Partial<Context>) {
	const pinia = createPinia();
	pinia.use(piniaPluginPersistedstate);

	if (process.server && context.req?.cookies) {
		server_createPiniaStore(pinia, context.req.cookies);
	}

	if (process.client) {
		await client_createPiniaStore(pinia);
	}

	return pinia;
}

function server_createPiniaStore(pinia: Pinia, cookies: NonNullable<Context['req']>['cookies']) {
	const savedState: any = {};

	const userCookie = cookies[`${STORE_NAMESPACE}_user`] || cookies[`${STORE_NAMESPACE}/user`];
	if (userCookie) {
		try {
			savedState.user = JSON.parse(userCookie);
		} catch {
			console.error(`[piniaStore.ts] createStore error parsing ${STORE_NAMESPACE}_user`, userCookie);
		}
	}

	const token = cookies['access_token'];

	if (token) {
		if (!savedState?.user) {
			savedState.user = {};
		}
		savedState.user.accessToken = token;
	}

	if (savedState.user) {
		const userStore = useUserStore(pinia);
		userStore.$patch(savedState.user);
	}
}

async function client_createPiniaStore(pinia: Pinia) {
	let cookieHasAuth = false; // track if cookies indicate authenticated state
	let didSyncLogoutFromCookies = false; // track if we explicitly synced logout state from cookies
	let didSyncLoginFromCookies = false; // track if we explicitly synced login state from cookies

	// Storage migration
	// check if anything needs to be migrated from the old localStorage
	const { needsLocalStorageMigration } = await import('@/migrate-local-storage');
	// from old structure (localStorage) to new structure (localStorage)
	if (await needsLocalStorageMigration()) {
		const { migrateLocalStorage } = await import('@/migrate-local-storage');
		await migrateLocalStorage();
	}
	let savedUser: Record<string, any> = JSON.parse(localStorage.getItem(`${STORE_NAMESPACE}/user`) ?? '{}');

	// Always check cookies for cross-domain auth sync (not just on subdomains)
	// Cookies are the source of truth for auth state across all domains
	const userCookie = Cookies.get(`${STORE_NAMESPACE}_user`) || Cookies.get(`${STORE_NAMESPACE}/user`);
	const accessTokenCookie = Cookies.get('access_token');

	// Parse cookie to check for actual auth state
	let parsedCookieUser: any = null;
	let cookieUserJwLoginId: string | undefined;
	if (userCookie) {
		try {
			parsedCookieUser = JSON.parse(userCookie);
			cookieUserJwLoginId = parsedCookieUser?.jwLoginId;
		} catch (err) {
			const { captureMessageForSentry } = await import('@/helpers/sentry-helper');

			captureMessageForSentry(
				'[store.ts] Error parsing user cookie',
				{ err, where: 'store.ts createStore (cookie parsing)' },
				'error'
			);
		}
	}

	// Detect if cookies and localStorage are out of sync
	// Cookie has auth if: user cookie exists + has jwLoginId + accessToken cookie exists
	cookieHasAuth = !!(userCookie && cookieUserJwLoginId && accessTokenCookie);
	const localStorageHasAuth = !!(savedUser?.jwLoginId && savedUser?.accessToken);

	// Sync from cookies if they're out of sync
	if (cookieHasAuth !== localStorageHasAuth) {
		if (cookieHasAuth) {
			// Cookie says logged in, localStorage says logged out → Sync login state

			// Start with cookie data as base (has all necessary fields including jwId)
			// Then merge in localStorage preferences to preserve user customizations
			// Note: Only include properties that are actually persisted to cookies (see persist-state.plugin.ts)
			const authFieldsFromCookie = {
				jwId: parsedCookieUser.jwId,
				jwLoginId: parsedCookieUser.jwLoginId,
				accessToken: accessTokenCookie,
				loggedInProviders: parsedCookieUser.loggedInProviders ?? [],
				isPremium: parsedCookieUser.isPremium,
				userRole: parsedCookieUser.userRole,
				onboardingStatus: parsedCookieUser.onboardingStatus,
				lastUsedLoginProvider: parsedCookieUser.lastUsedLoginProvider,
				accountCreationTime: parsedCookieUser.accountCreationTime,
			};

			// Merge: cookie as base, then layer localStorage on top, then auth fields override
			savedUser = {
				...parsedCookieUser, // Base: all fields from cookie
				...savedUser, // Override: preferences from localStorage
				...authFieldsFromCookie, // Final override: auth fields from cookie
			};

			// Update LocalForage to ensure persistence layer has correct data
			await LocalForage.setItem('user', savedUser);
			localStorage.setItem('jw/user', JSON.stringify(savedUser));

			// Track that we explicitly synced login state from cookies
			// This is used later to prevent server baseState from causing hydration mismatches
			didSyncLoginFromCookies = true;
		} else {
			// Cookie says logged out, localStorage says logged in → Sync logout state

			// Preserve existing user object, only clear auth-related fields
			if (savedUser) {
				savedUser.jwLoginId = undefined;
				savedUser.accessToken = undefined;
				savedUser.loggedInProviders = [];
				savedUser.isPremium = false;
				savedUser.userRole = 'USER';
				savedUser.info = {};
				savedUser.profileInfo = null;
				savedUser.onboardingStatus = 'GLOBAL';
				savedUser.lastUsedLoginProvider = parsedCookieUser?.lastUsedLoginProvider || '';
				savedUser.accountCreationTime = null;
				savedUser.profileIsComplete = false;
			}

			await LocalForage.setItem('user', savedUser);
			localStorage.setItem('jw/user', JSON.stringify(savedUser));

			// Track that we explicitly synced logout state from cookies
			// This is used later to determine if we should ignore SSR baseState
			didSyncLogoutFromCookies = true;
		}
	}

	//XSS-safe deserialization of uneval result: https://github.com/Rich-Harris/devalue?tab=readme-ov-file#xss-mitigation
	const baseState = (0, eval)('(' + (window?.__DATA__ as any)?.piniaState + ')') ?? {};

	let shouldIgnoreBaseState = false;
	if (didSyncLogoutFromCookies || didSyncLoginFromCookies) {
		// Check if server baseState auth matches cookie auth
		const baseStateHasAuth = !!(baseState.user?.jwLoginId && baseState.user?.accessToken);
		const authStatesMatch = cookieHasAuth === baseStateHasAuth;

		// Only ignore baseState if auth states don't match (server stale)
		// If they match, baseState is fresh and we should use it for settings
		shouldIgnoreBaseState = !authStatesMatch;
	}

	if (shouldIgnoreBaseState) {
		baseState.user = {};
	}

	const isBaseDataEmpty = Object.keys(baseState.user || {}).length === 0;

	savedUser = {
		...savedUser,
		...(isBaseDataEmpty
			? {}
			: {
					settings: baseState?.user?.settings,
					preferences: baseState?.user?.preferences,
					newBuyboxFilterType: baseState?.user?.newBuyboxFilterType,
					newBuyboxView: baseState?.user?.newBuyboxView,
					buyboxExpView: baseState?.user?.buyboxExpView,
			  }),
	};

	// SAVE INITIAL USER STATE;
	if (window) {
		(window as any).__INITIAL_SAVED_USER_STATE__ = JSON.parse(JSON.stringify(savedUser));
	}

	baseState.user = {
		...(baseState.user || {}),
		...(savedUser || {}),
	};

	pinia.state.value = baseState;
}
